<template>
  <div class="showcase">
    <promo-video-popup v-if="$store.state.isVideoShowing" />

    <responsive-image
      v-for="background in backgrounds"
      :key="`${background.default}-${background.media || 'default'}`"
      :image-data="background"
      :class="{
        'background--gradient': background.shouldShowGradient,
        'background--small': background.isSmall
      }"
      class="background-wrap" />

    <div v-if="shouldShowAppAvailable">
      <app-available-notification @close="closeAppAvailable" />
    </div>

    <main-header
      :is-game-menu-opened="isGameMenuOpened"
      :is-burger-active="isBurgerActive"
      :is-sidebar-visible="isSidebarAvailable"
      :class="{ 'root__header--active': isGameMenuOpened }"
      @hideSidebar="setBurgerState(false)"
      @setBurgerState="setBurgerState"
      @setGameMenuState="setGameMenuState" />

    <div
      ref="contentWrapper"
      :class="{
        'root__app--has-sidebar': isSidebarAvailable,
        'root__app--centered': isCenteredContent,
      }"
      class="root__app">
      <main-sidebar
        :is-showing="isSidebarVisible"
        class="root__sidebar"
        @hideSidebar="setBurgerState(false)" />

      <page-wrapper class="root__view">
        <router-view
          class="root__content" />
      </page-wrapper>
    </div>

    <customer-footer
      class="root__footer"
      :is-sidebar-visible="isSidebarAvailable" />

    <scroll-up />

    <altcraft-container v-if="!isPlatformApp" />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import IntercomApiDriver from 'Root/drivers/IntercomApiDriver';
import ResponsiveImage from 'Components/ui/Image/ResponsiveImage.vue';
import MainSidebar from 'Components/Sidebar/MainSidebar.vue';
import MainHeader from 'Components/MainHeader/MainHeader.vue';
import ScrollUp from 'Components/ScrollUp.vue';
import CustomerFooter from 'Components/CustomerFooter.vue';
import {
  flatTagsById, rootTagsByGames, setFlatTagsById, setRootTagsByGames,
} from 'Root/helpers/tags';
import PageWrapper from 'Components/PageWrapper.vue';
import PromoVideoPopup from 'Components/Popups/PromoVideoPopup.vue';
import AltcraftContainer from 'Components/Popups/altcraftPopups/AltcraftContainer.vue';
import AppAvailableNotification from 'Components/ui/AppMarkets/AppAvailableNotification.vue';
import axios from 'axios';
import AppModel from 'Models/AppModel';
import HttpErrorModel from 'Models/HttpErrorModel';
import AmplitudeAnalyticsDriver from 'Root/drivers/AmplitudeAnalyticsDriver';

const { API_URI } = AppModel;

export default {
  name: 'Showcase',

  components: {
    AppAvailableNotification,
    ResponsiveImage,
    MainSidebar,
    MainHeader,
    ScrollUp,
    CustomerFooter,
    PageWrapper,
    PromoVideoPopup,
    AltcraftContainer,
  },

  props: {
    showCookieNotification: {
      type: Boolean,
      default: false,
    },
  },

  asyncData({ store, router }) {
    const promises = [store.dispatch('products/getData')];

    const route = router.currentRoute;
    if (route.name === 'game__service') {
      const { categories, namespace } = route.params;
      if (namespace === store.state.activeGame.routeNamespace) {
        if (categories && typeof categories === 'string') {
          promises.push(axios.get(`${API_URI}/tag`, {
            params: {
              seo_url: categories.split('/').at(-1),
              game_id: store.state.activeGame.id,
            },
          })
            .then(({ data }) => {
              store.commit('products/setActiveTag', data);
            }).catch((e) => {
              console.log(e.message);
            }));
        }
      }
    }
    return Promise.all(promises);
  },

  data() {
    return {
      isBurgerActive: false,
      isSidebarVisible: false,
      isGameMenuOpened: false,
      showAppAvailable: false,
    };
  },

  computed: {
    ...mapState(['activeGame', 'user', 'backgrounds']),
    ...mapGetters(['isPlatformApp']),
    ...mapState('products', ['product', 'tags']),

    is404() {
      return this.$store.state.httpErrorCode === HttpErrorModel.NOT_FOUND;
    },

    shouldShowAppAvailable() {
      return this.$route.path === '/' && this.showAppAvailable && !!this.user;
    },

    canSeeSidebar() {
      if (this.$isSSR) {
        return false;
      }

      if (this.$store.state.userDevice.innerWidth < 1024) {
        return this.isGameMenuOpened || this.isBurgerActive;
      }

      return this.isSidebarAvailable;
    },

    isSidebarAvailable() {
      return (['game__root', 'game__service'].includes(this.$route.name)
          || this.product?.product_currency_setting_id
          || this.product?.meta?.servers) /* TODO: удалить после теста валюты на проде SKY-3296 */
          && !this.is404;
    },

    isCenteredContent() {
      return this.$route.meta?.isCenteredPage;
    },
  },

  watch: {
    canSeeSidebar(newValue) {
      this.isSidebarVisible = newValue;
    },
  },

  mounted() {
    if (!localStorage.getItem('app-available-closed')) {
      this.showAppAvailable = true;
    }
    this.isSidebarVisible = this.isSidebarAvailable && window.innerWidth > 1024;

    if ('open_intercom' in this.$route.query) {
      IntercomApiDriver.showNewMessage();
    }
  },

  created() {
    if (typeof window !== 'undefined') {
      const amplitudeApiKey = process.env.VUE_APP_AMPLITUDE_API_KEY;

      if (amplitudeApiKey && process.env.NODE_ENV === 'production') {
        AmplitudeAnalyticsDriver.init(amplitudeApiKey);
      } else {
        console.warn('Amplitude Analytics Driver not initialized (either no API key or not in production).');
      }

      if (!Object.keys(flatTagsById).length) {
        setFlatTagsById(this.tags);
      }

      if (!Object.keys(rootTagsByGames).length) {
        setRootTagsByGames();
      }
    }
  },

  methods: {
    closeAppAvailable() {
      localStorage.setItem('app-available-closed', '1');
      this.showAppAvailable = false;
    },

    setGameMenuState(state) {
      this.isGameMenuOpened = state;

      this.setBodyScroll(state);
    },

    setBurgerState(state) {
      this.isBurgerActive = state;

      this.setBodyScroll(state);
    },

    setBodyScroll(state) {
      if (state) {
        document.body.classList.add('no-scroll');
      } else {
        document.body.classList.remove('no-scroll');
      }
    },
  },
};
</script>

<style lang="scss">
  .root.has-cookie-notice .background-wrap {
    top: #{$size-mobile-header-height + $size-pc-cookie};
  }

  @media screen and #{$screen-more-or-1366} {
    .root.has-cookie-notice .background-wrap {
      top: #{$size-pc-header-height + $size-pc-cookie};
    }

    .root.has-cookie-notice .root__content .intro__game {
      height: calc(100vh - #{$size-pc-header-height + $size-pc-cookie}) !important;
    }
  }
</style>

<style lang="scss" scoped>
  .root__content {
    position: relative;
  }

  .background-wrap {
    position: absolute;
    max-height: 350px;
    overflow: hidden;
    height: 100%;
    top: $size-mobile-header-height;
    z-index: -1;
  }

  .background--gradient:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -10%;
    width: 100%;
    background-image: linear-gradient(180deg, rgba($color--basic-black, 0) 0%, $color--basic-black 50%);
    height: 150px;
  }

  .root__app {
    display: grid;
    justify-content: center;
    grid-template-columns: 1fr minmax(auto, $size-container-max-width) 1fr;
  }

  .root__app--centered {
    .root__view {
      grid-row: 1;
      grid-column: 2;
    }
  }

  .root__view {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    min-width: 50%;
    position: relative;
    grid-row: 1;
    grid-column: 1 / -1;
  }

  @media screen and #{$screen-more-or-1024} {
    .root__sidebar {
      grid-row: 1;
      grid-column: 1;
    }

    .root__app--has-sidebar {
      grid-template-columns: minmax(250px, 1fr) minmax(auto, $size-container-max-width) 1fr;

      .root__sidebar {
        width: $size-pc-sidebar !important;
      }
    }

    .background--small {
      max-height: 400px;
    }

    .root__sidebar {
      justify-self: end;
    }

    .background-wrap__item {
      overflow: visible;
    }

    .background--gradient:after {
      height: 250px;
    }

    .root__header--active {
      z-index: 101 !important; // higher than sidebar

      & ~ .root__app,
      & ~ .root__footer {
        filter: blur(5px);
        z-index: 2;
        pointer-events: none;
      }
    }
  }

  @media screen and #{$screen-more-or-1366} {
    .background-wrap {
      max-height: 670px;
      top: $size-pc-header-height;

      .responsive-image__img {
        height: 100%;
        object-fit: cover;
        object-position: top;
      }
    }
  }
</style>
